import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useAccessToken } from './useAxiosAuth';
import { useMemo, useCallback } from 'react';
import { useConfig } from '@/contexts/ConfigContext';
import { alltixParamsSerializer } from '@/helpers/axiosParams';
import { useSelfServiceAuth } from '@/stores/selfServiceAuth';
import { markSessionEnd } from '@/utils/reportApiError';

const isSelfServicePublicUrl = (url?: string) =>
  url?.startsWith('/self-service/auth/request') ||
  url?.startsWith('/self-service/auth/verify');

export const useAxiosAlltix = <T>(): ((
  config: AxiosRequestConfig,
) => Promise<T>) => {
  const getAccessToken = useAccessToken();
  const { baseUrl } = useConfig();

  const trafikportalenAxios = useMemo(() => {
    const instance = Axios.create({
      baseURL: baseUrl,
      paramsSerializer: alltixParamsSerializer,
    });

    instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (
          error.response?.status === 401 &&
          error.config?.url?.startsWith('/self-service/') &&
          !isSelfServicePublicUrl(error.config.url)
        ) {
          useSelfServiceAuth.getState().logout();
          markSessionEnd(error);
        }
        return Promise.reject(error);
      },
    );

    return instance;
  }, [baseUrl]);

  return useCallback(
    async (config: AxiosRequestConfig) => {
      const source = Axios.CancelToken.source();

      const isPublicRoute = config.url?.startsWith('/public/');
      const isSelfServicePublic = isSelfServicePublicUrl(config.url);
      const isSelfServiceRoute = config.url?.startsWith('/self-service/');

      let authToken: string | undefined;

      if (isPublicRoute || isSelfServicePublic) {
        // No auth needed
      } else if (isSelfServiceRoute) {
        authToken = useSelfServiceAuth.getState().token;
      } else {
        authToken = await getAccessToken();
      }

      if (authToken) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${authToken}`,
        };
      }

      const promise = trafikportalenAxios({
        ...config,
        cancelToken: source.token,
      }).then(({ data }) => data);

      // @ts-expect-error allow cancellations
      promise.cancel = () => {
        console.log('Query was cancelled by React Query');
        source.cancel('Query was cancelled by React Query');
      };

      return promise;
    },
    [getAccessToken, trafikportalenAxios],
  );
};

export type ErrorType<Error> = AxiosError<Error>;
